import { CSSInterpolation } from '@emotion/css/create-instance'
import { cx } from '@emotion/css'
import React from 'react'

import { UnstyledButton } from '../../components/UnstyledButton'
import { CssVars, NeutralColorToken } from '../themes'
import { FoundationIcon } from './FoundationIcon'
import type { Icon12 } from './FoundationIcon/icons'
import { HoverState } from './HoverState'
import { Type } from './Type'
import { useUniqueId } from './useUniqueId'

type TabType<T extends string> = {
  /**
   * The `string` value for this tab, used to track selected state; must be unique.
   */
  value: T
  /**
   * The `string` text to display for the tab.
   */
  label: string
  /**
   * The `Icon12` to display for the tab.
   */
  icon?: Icon12
}

export function DO_NOT_USE_TabTypeJustForPropsDocs<T extends string>(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _props: TabType<T>
) {
  return null
}

type Props<T extends string> = {
  /**
   * The array of tabs to display; length of either `2` or `3`
   *
   * Highly recommended that you input inline; if using a variable, you may have to type with `ComponentProps<typeof SegmentedControl>['tabs']`
   */
  tabs: [TabType<T>, TabType<T>, TabType<T>?]
  /**
   * Called when user selects a different tab; that tab's `value` is passed to `onChange`.
   */
  onChange: (value: T) => void
  /**
   * The `value` of the currently selected tab.
   */
  selectedValue: string
  /**
   * Passed to SegmentedControl as an [aria-label](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-label);
   * highly recommended for accessibility purposes.
   */
  ariaLabel?: string
  /**
   * Passed to child radio `input`s as `name`.
   */
  inputName?: string
  /**
   * Pass a CSS object if you need to override styles on the root element. Please do this sparingly.
   */
  overrideRootStyles?: CSSInterpolation
}

/**
 * A Segmented Control allows the user to toggle between equally important views or settings.
 * Segmented Controls can have two or three segments, each of which functions as a mutually exclusive button.
 * Segments can have individual icons. By default, will fill to margin.
 */
export function SegmentedControl<T extends string>({
  ariaLabel = 'SegmentedControl',
  inputName,
  onChange,
  overrideRootStyles,
  selectedValue,
  tabs,
}: Props<T>) {
  const id = useUniqueId()

  return (
    <div
      aria-label={ariaLabel}
      css={[{ display: 'flex' }, overrideRootStyles]}
      role="tablist"
    >
      {tabs
        .filter((tab): tab is TabType<T> => !!tab)
        .map(tab => {
          const { icon, label, value } = tab
          const uniqueValue = `${id}-${value}`
          const isActive = value === selectedValue
          const labelColor = isActive
            ? NeutralColorToken.Bg1
            : NeutralColorToken.Fg1

          return (
            <HoverState key={uniqueValue} color={NeutralColorToken.Bg1}>
              {({ colorVariable, hoverableClassName, rootClassName }) => (
                <UnstyledButton
                  aria-selected={isActive}
                  className={cx(rootClassName, hoverableClassName)}
                  css={[
                    {
                      ':first-of-type': {
                        borderRadius: '24px 0 0 24px',
                        borderRight: 0,
                        paddingLeft: 12,
                      },
                      ':last-of-type': {
                        borderLeft: 0,
                        borderRadius: '0 24px 24px 0',
                        paddingRight: 12,
                      },
                      alignItems: 'center',
                      background: isActive
                        ? CssVars.neutral.fg1
                        : colorVariable,
                      border: `1px ${CssVars.neutral.fg1} solid`,
                      boxSizing: 'border-box',
                      cursor: 'pointer',
                      display: 'flex',
                      flex: 1,
                      justifyContent: 'center',
                      padding: '5px 8px', //  Note (jonathan.wang1) 32 is Figma height, so padding is [32 - 20 (text S) - 2 (border)] / 2 = 5
                      width: '100%',
                    },
                  ]}
                  onClick={() => onChange(value)}
                  role="tab"
                >
                  <input
                    checked={isActive}
                    css={[
                      {
                        opacity: 0,
                        position: 'absolute',
                        zIndex: -1,
                      },
                    ]}
                    id={uniqueValue}
                    name={inputName || id}
                    onChange={() => onChange(value)}
                    type="radio"
                  />
                  <label
                    css={{
                      alignItems: 'center',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'row',
                      pointerEvents: 'auto',
                    }}
                    htmlFor={uniqueValue}
                  >
                    {icon ? (
                      <div css={{ paddingRight: '8px' }}>
                        <FoundationIcon color={labelColor} type={icon} />
                      </div>
                    ) : null}
                    <Type.Text
                      color={labelColor}
                      isBold={isActive}
                      maxLines={1}
                      size="S"
                    >
                      {label}
                    </Type.Text>
                  </label>
                </UnstyledButton>
              )}
            </HoverState>
          )
        })}
    </div>
  )
}
